/**
 * Text with the server's custom emoji drawn in it.
 *
 * This used to live inside chat.tsx, which meant `:hype:` became a picture in
 * a message and stayed as five letters of punctuation everywhere else - in a
 * status, on a party card, in an announcement, under somebody's name. It is
 * one component now, and every surface that shows text a person wrote uses
 * it.
 *
 * Deliberately not a chat message: no links, no mentions, no big-emoji rule.
 * A party title is a line of text, and turning a URL inside one into a link
 * you cannot click is worse than leaving it alone. Chat keeps its own richer
 * renderer on top of the same emoji lookup.
 */

import { useSession } from '@/stores/session';
import { EmojiImg } from './EmojiImg';

/** Splits on :name: without touching anything else. */
const TOKEN = /(:[a-z0-9_-]{2,32}:)/gi;

export function EmojiText({ text, size, className, playing }: {
  text: string | null | undefined;
  /** Pixel height, when the surrounding text is not body size. */
  size?: number;
  className?: string;
  /** True while the pointer is over whatever this sits in - animated emoji
      hold still until then, exactly as they do in the log. */
  playing?: boolean;
}) {
  // Subscribed rather than read once: the set arrives after the first paint,
  // and a module-level lookup would leave every :name: as plain text until
  // something else happened to re-render.
  const custom = useSession((s) => s.emoji);
  const body = text ?? '';

  // Nothing that could be an emoji: skip the split entirely. This runs on
  // every name and every card in a long list, so the common case does no work.
  if (!body.includes(':') || !custom.length) {
    return <span className={className}>{body}</span>;
  }

  return (
    <span className={className}>
      {body.split(TOKEN).map((part, i) => {
        const name = /^:([a-z0-9_-]{2,32}):$/i.exec(part)?.[1];
        const emoji = name
          ? custom.find((e) => e.name.toLowerCase() === name.toLowerCase())
          : null;

        return emoji
          ? <EmojiImg key={i} url={emoji.url} label={part} size={size} playing={playing} />
          : <span key={i}>{part}</span>;
      })}
    </span>
  );
}
